import { FC } from "react";
import {
  Box,
  Typography,
  Grid,
  Card,
  CardContent,
  CardMedia,
  FormControlLabel,
  Checkbox,
  Tooltip,
  Divider,
  Chip,
  Paper,
} from "@mui/material";
import { MsdDto } from "src/app/services/generatedApi";
import { defaultMsdData } from "src/app/logic/assessment/msd/constants";

interface MsdBodyZoneProps {
  title: string;
  bodyZone: keyof MsdDto;
  riskFactors: {
    path: string;
    displayName: string;
    image: string;
    description: string;
    thresholds: {
      propertyName: string;
      displayName: string;
      isHazard: boolean;
    }[];
  }[];
  msdData?: MsdDto;
  handleUpdate: (
    bodyZone: keyof MsdDto,
    category: string,
    propertyName: string,
    value: boolean,
  ) => void;
}

const getCategoryDisplay = (category: string): string => {
  const categoryDisplayMap: Record<string, string> = {
    posture: "Posture",
    force: "Weight/Force",
    pinch: "Pinch Grip",
    grip: "Grasp Grip",
    motion: "Repetitive Motion",
    impact: "Impact",
    vibration: "Vibration",
    neckPosture: "Neck Posture",
    shoulderPosture: "Shoulder Posture",
  };

  return (
    categoryDisplayMap[category] ||
    category.charAt(0).toUpperCase() + category.slice(1)
  );
};

export const MsdBodyZone: FC<MsdBodyZoneProps> = ({
  title,
  bodyZone,
  riskFactors,
  msdData = defaultMsdData,
  handleUpdate,
}) => {
  const getValue = (category: string, propertyName: string): boolean => {
    const categoryData =
      msdData[bodyZone]![category as keyof (typeof msdData)[typeof bodyZone]];
    if (!categoryData) return false;
    return (categoryData as any)[propertyName] || false;
  };

  const handleCheckboxChange = (
    category: string,
    propertyName: string,
    value: boolean,
  ) => {
    handleUpdate(bodyZone, category, propertyName, value);
  };

  const groupedRiskFactors: Record<string, typeof riskFactors> = {};

  riskFactors.forEach((factor) => {
    const category = factor.path.split(".")[0];
    if (!groupedRiskFactors[category]) {
      groupedRiskFactors[category] = [];
    }
    groupedRiskFactors[category].push(factor);
  });

  return (
    <Box>
      <Typography variant="h5" gutterBottom>
        {title}
      </Typography>

      {Object.entries(groupedRiskFactors).map(([category, factors]) => (
        <Box key={category} sx={{ mb: 4 }}>
          <Paper
            elevation={0}
            sx={{
              p: 2,
              mb: 2,
              bgcolor: "primary.light",
              color: "primary.contrastText",
            }}
          >
            <Typography variant="h6">{getCategoryDisplay(category)}</Typography>
          </Paper>

          <Grid container spacing={3}>
            {factors.map((factor) => (
              <Grid item xs={12} md={6} key={factor.path}>
                <Card variant="outlined" sx={{ height: "100%" }}>
                  <Grid container>
                    <Grid item xs={12} md={4}>
                      <CardMedia
                        component="img"
                        image={factor.image}
                        alt={factor.displayName}
                        sx={{
                          height: "100%",
                          objectFit: "cover",
                          maxHeight: 200,
                          width: "100%",
                        }}
                      />
                    </Grid>
                    <Grid item xs={12} md={8}>
                      <CardContent>
                        <Typography variant="subtitle1" gutterBottom>
                          {factor.displayName}
                        </Typography>
                        <Typography
                          variant="body2"
                          color="text.secondary"
                          paragraph
                        >
                          {factor.description}
                        </Typography>

                        <Divider sx={{ mb: 1.5 }} />

                        {factor.thresholds.map((threshold) => {
                          const isChecked = getValue(
                            category,
                            threshold.propertyName,
                          );

                          return (
                            <Tooltip
                              key={threshold.propertyName}
                              title={threshold.displayName}
                              placement="top"
                            >
                              <Box>
                                <FormControlLabel
                                  control={
                                    <Checkbox
                                      checked={isChecked}
                                      onChange={(e) =>
                                        handleCheckboxChange(
                                          category,
                                          threshold.propertyName,
                                          e.target.checked,
                                        )
                                      }
                                      color={
                                        threshold.isHazard ? "error" : "warning"
                                      }
                                    />
                                  }
                                  label={
                                    <Box
                                      sx={{
                                        display: "flex",
                                        alignItems: "center",
                                        justifyContent: "space-between",
                                        width: "100%",
                                      }}
                                    >
                                      <Typography variant="body2">
                                        {threshold.displayName
                                          .replace("(Hazard)", "")
                                          .replace("(Caution)", "")}
                                      </Typography>
                                      <Chip
                                        label={
                                          threshold.isHazard
                                            ? "Hazard"
                                            : "Caution"
                                        }
                                        size="small"
                                        color={
                                          threshold.isHazard
                                            ? "error"
                                            : "warning"
                                        }
                                        sx={{ ml: 1 }}
                                        variant={
                                          isChecked ? "filled" : "outlined"
                                        }
                                      />
                                    </Box>
                                  }
                                  sx={{
                                    width: "100%",
                                    ml: -1,
                                    mb: 0.5,
                                    py: 0.5,
                                    borderRadius: 1,
                                    bgcolor: isChecked
                                      ? threshold.isHazard
                                        ? "rgba(229, 115, 115, 0.1)"
                                        : "rgba(255, 241, 118, 0.1)"
                                      : "transparent",
                                  }}
                                />
                              </Box>
                            </Tooltip>
                          );
                        })}
                      </CardContent>
                    </Grid>
                  </Grid>
                </Card>
              </Grid>
            ))}
          </Grid>
        </Box>
      ))}
    </Box>
  );
};
